/**
 * The editor's writes, as row patches (PLAN §6.1): create, set fields, edit a
 * script, move, soft delete. Pure over the session's `Writer`: each builder
 * stamps a fresh HLC and clientOpId, and a block of moves shares one HLC
 * (PLAN §6.2 "A drags a block of 5 parts: one multi-row patch batch that
 * shares a single HLC").
 */
import type { RowKind } from '@acp/domain/fields.js';
import { ordBetween } from '@acp/domain/ordering.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import type { Row, RowPatch } from '@acp/domain/row.js';
import { textReplaceUpdate } from '@acp/domain/text.js';
import { childrenOf, type RowTree } from '@acp/domain/tree.js';
import { PIECE_TYPES } from './pieceTypes.js';
import type { Writer } from './writer.js';

export type Move = { readonly rowId: string; readonly parentId: string; readonly ord: string };

/** What a new row of each kind starts with, so it is recognisable in the tree before anyone names it. */
export function initialFields(kind: 'segment' | 'part' | 'piece' | 'adlib'): Record<string, JsonValue> {
  switch (kind) {
    case 'segment':
      return { name: 'New segment' };
    case 'part':
      return { title: 'New part' };
    case 'piece':
    case 'adlib': {
      const first = PIECE_TYPES[0];
      const fallback = kind === 'piece' ? 'New piece' : 'New adlib';
      return first === undefined ? { name: fallback } : { name: first.label, pieceType: first.id, sourceLayer: first.defaultSourceLayer };
    }
  }
}

/**
 * The `ord` for a new child of `parentId` right after `afterId` (null: at the
 * end), strictly between its visible neighbours.
 */
export function ordForInsert(tree: RowTree, parentId: string, afterId: string | null, writerTag: string): string {
  const siblings = childrenOf(tree, parentId);
  if (afterId === null) return ordBetween(siblings.at(-1)?.ord ?? null, null, writerTag);
  const index = siblings.findIndex((r) => r.rowId === afterId);
  if (index < 0) return ordBetween(siblings.at(-1)?.ord ?? null, null, writerTag);
  return ordBetween(siblings[index]?.ord ?? null, siblings[index + 1]?.ord ?? null, writerTag);
}

export function createPatch(writer: Writer, rundownId: string, kind: RowKind, parentId: string, ord: string, set: Readonly<Record<string, JsonValue>>): RowPatch {
  return {
    rowId: writer.newId(),
    set: { ...set, parentId, ord },
    create: { kind, rundownId, source: { system: 'native' }, lockedFields: [] },
    hlc: writer.clock.now(),
    origin: writer.nodeId,
    clientOpId: writer.newOpId(),
  };
}

export function setPatch(writer: Writer, rowId: string, set: Readonly<Record<string, JsonValue>>): RowPatch {
  return { rowId, set: { ...set }, hlc: writer.clock.now(), origin: writer.nodeId, clientOpId: writer.newOpId() };
}

/** Soft delete (PLAN §6.1): `deleted: true`, one register; the rows sharing one HLC. */
export function deletePatches(writer: Writer, rowIds: readonly string[]): RowPatch[] {
  const hlc = writer.clock.now();
  return rowIds.map((rowId) => ({ rowId, set: { deleted: true }, hlc, origin: writer.nodeId, clientOpId: writer.newOpId() }));
}

/** A block move: every row's `parentId` + `ord` under one HLC. */
export function movePatches(writer: Writer, moves: readonly Move[]): RowPatch[] {
  const hlc = writer.clock.now();
  return moves.map((m) => ({ rowId: m.rowId, set: { parentId: m.parentId, ord: m.ord }, hlc, origin: writer.nodeId, clientOpId: writer.newOpId() }));
}

/**
 * A script (text field) edit: the Yjs update that turns the row's current
 * text state into `next`, replacing only the span that differs — so what
 * someone else typed concurrently elsewhere survives the merge (PLAN §6.2
 * "Two people edit the same script"). Null when nothing changed.
 */
export function textPatch(writer: Writer, row: Row, field: string, next: string): RowPatch | null {
  const current = typeof row.f[field] === 'string' ? row.f[field] : '';
  if (current === next) return null;
  const state = Object.hasOwn(row.text, field) ? (row.text[field] ?? null) : null;
  const update = textReplaceUpdate(state, next, writer.textClientId());
  return { rowId: row.rowId, set: {}, text: { [field]: update }, hlc: writer.clock.now(), origin: writer.nodeId, clientOpId: writer.newOpId() };
}
